import { ChartData, ChartOptions, Scale } from 'chart.js'

/**
 * Derives the options for the scrolling plot canvas: its y scale keeps rendering the grid lines, but is collapsed to
 * zero width because the ticks are rendered by the pinned axis canvas instead
 * @param {ChartOptions<'bar'>} options
 */
export const getPlotOptions = (options: ChartOptions<'bar'>): ChartOptions<'bar'> => ({
  ...options,
  scales: {
    ...options.scales,
    y: {
      ...options.scales?.y,
      ticks: { ...options.scales?.y?.ticks, display: false },
      afterFit: (scale: Scale) => {
        scale.width = 0
      }
    }
  }
})

/**
 * Derives the options for the pinned axis canvas: an inert copy of the chart that only paints the y scale. Its width is
 * pinned so the ticks end exactly at the edge of the axis column. Everything the plot canvas already paints is made
 * transparent rather than hidden, because the canvas reaches past the axis column and because the scales must keep the
 * exact dimensions they have on the plot canvas to stay vertically aligned with it
 * @param {ChartOptions<'bar'>} options
 * @param {number} yAxisWidth
 */
export const getAxisOptions = (options: ChartOptions<'bar'>, yAxisWidth: number): ChartOptions<'bar'> => ({
  ...options,
  events: [],
  scales: {
    ...options.scales,
    y: {
      ...options.scales?.y,
      grid: { ...options.scales?.y?.grid, color: 'transparent' },
      afterFit: (scale: Scale) => {
        scale.width = yAxisWidth
      }
    },
    x: {
      ...options.scales?.x,
      grid: { ...options.scales?.x?.grid, color: 'transparent' },
      ticks: { ...options.scales?.x?.ticks, color: 'transparent' }
    }
  },
  plugins: {
    ...options.plugins,
    tooltip: { ...options.plugins?.tooltip, enabled: false, external: undefined }
  }
})

/**
 * Derives the data for the pinned axis canvas: the real values, so the y scale resolves to the exact same ticks as the
 * plot canvas, but without painting any bars
 * @param {ChartData<'bar'>} data
 */
export const getAxisData = (data: ChartData<'bar'>): ChartData<'bar'> => ({
  ...data,
  datasets: data.datasets.map((dataset) => ({ ...dataset, backgroundColor: 'transparent' }))
})
